import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, LinearProgress, MenuItem, Paper, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import backupService from '../services/BackupService';

const jst = (seconds) => new Intl.DateTimeFormat('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'medium', timeStyle: 'short' }).format(new Date(Number(seconds) * 1000));

export function BackupProgress({ job, compact = false }) {
    const { t } = useTranslation();
    const progress = job.progress || {};
    const stage = progress.stage || (job.status === 'running' ? 'preparing' : job.status);
    const running = ['queued', 'running'].includes(job.status);
    const total = Number(progress.total || 0);
    const completed = Number(progress.completed || 0);
    const measurable = total > 0;
    const value = job.status === 'completed' ? 100 : measurable ? Math.min(100, Math.max(0, completed / total * 100)) : 0;
    return <Stack spacing={0.5} sx={{ minWidth: compact ? 160 : undefined }}>
        <Typography variant="body2">{t(`backups.stages.${stage}`, stage)}</Typography>
        {progress.safetySnapshot && <Typography variant="caption">{t('backups.savingSafety')}</Typography>}
        <LinearProgress aria-label={t('backups.progressTitle')}
            variant={running && !measurable ? 'indeterminate' : 'determinate'} value={value}
            color={job.status === 'failed' ? 'error' : job.status === 'completed' ? 'success' : 'primary'} />
        {progress.unit && <Typography variant="caption">{progress.total !== undefined ? `${completed} / ${total}` : completed} {t(`backups.progressUnits.${progress.unit}`, progress.unit)}</Typography>}
        {progress.records !== undefined && <Typography variant="caption">{t('backups.recordsBackedUp', { count: Number(progress.records) })}</Typography>}
        {progress.currentItem && <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{progress.currentItem}</Typography>}
        {!compact && job.status === 'failed' && job.error && <Alert severity="error">{job.error}</Alert>}
    </Stack>;
}

export default function AdminClientBackups() {
    const { clientId } = useParams();
    const { t } = useTranslation();
    const [data, setData] = useState({ control: {}, snapshots: [], jobs: [] });
    const [snapshotId, setSnapshotId] = useState('');
    const [filePage, setFilePage] = useState({ files: [], total: 0 });
    const [query, setQuery] = useState('');
    const [searchRequest, setSearchRequest] = useState(null);
    const [searchStatus, setSearchStatus] = useState('idle');
    const [offset, setOffset] = useState(0);
    const [busy, setBusy] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [confirm, setConfirm] = useState(null);
    const locked = Boolean(data.control.lockJob);
    const progressJob = data.jobs.find(job => job.status === 'running') || data.jobs.find(job => job.status === 'queued') || data.jobs[0];
    const pending = data.jobs.some(job => ['queued', 'running'].includes(job.status));
    const disabled = busy || locked || pending || !loaded || !data.ready;
    const selectedSnapshot = data.snapshots.find(snapshot => snapshot.snapshotId === snapshotId);
    const hasSearch = searchRequest?.snapshotId === snapshotId && searchRequest?.clientId === clientId;
    const fileRows = (filePage.fileDetails || []).filter(file => file.category === 'document');

    const refresh = useCallback(async () => {
        const result = await backupService.request(clientId);
        setData(result);
        setLoaded(true);
        setSnapshotId(current => result.snapshots.some(s => s.snapshotId === current) ? current : (result.snapshots[0]?.snapshotId || ''));
    }, [clientId]);

    useEffect(() => {
        setLoaded(false);
        setData({ control: {}, snapshots: [], jobs: [] });
        setSnapshotId('');
        const load = () => refresh().catch(e => setError(e.message));
        load();
        const timer = setInterval(load, 5000);
        return () => clearInterval(timer);
    }, [refresh]);

    useEffect(() => {
        setQuery('');
        setSearchRequest(null);
        setSearchStatus('idle');
        setFilePage({ files: [], total: 0 });
        setOffset(0);
    }, [clientId, snapshotId]);

    useEffect(() => {
        let cancelled = false;
        if (!hasSearch) return undefined;
        setSearchStatus('loading');
        setFilePage({ files: [], total: 0 });
        backupService.request(clientId, `/snapshots/${encodeURIComponent(snapshotId)}/files?q=${encodeURIComponent(searchRequest.term)}&offset=${offset}&limit=50`)
            .then(result => {
                if (!cancelled) {
                    setFilePage(result);
                    setSearchStatus('done');
                }
            })
            .catch(e => {
                if (!cancelled) {
                    setError(e.message);
                    setSearchStatus('error');
                }
            });
        return () => { cancelled = true; };
    }, [clientId, snapshotId, searchRequest, hasSearch, offset]);

    const searchFiles = event => {
        event.preventDefault();
        if (!snapshotId || !query.trim()) return;
        setOffset(0);
        setError('');
        setSearchRequest({ term: query.trim(), snapshotId, clientId });
    };

    const downloadFile = async key => {
        setBusy(true);
        setError('');
        try {
            const result = await backupService.request(clientId, `/snapshots/${encodeURIComponent(snapshotId)}/download?fileKey=${encodeURIComponent(key)}`);
            const link = document.createElement('a');
            link.href = result.url;
            link.download = result.fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    const act = async (path, body, method = 'POST') => {
        setBusy(true);
        setError('');
        try {
            await backupService.request(clientId, path, method, body);
            setConfirm(null);
            await refresh();
        } catch (e) { setError(e.message); }
        finally { setBusy(false); }
    };

    return <Stack spacing={2}>
        {loaded && !data.ready && <Alert severity="info">{t('backups.awaitingSetup')}</Alert>}
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">{t('backups.title')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>{t('backups.schedule')}</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                <FormControlLabel label={t('backups.enabled')} control={<Switch checked={Boolean(data.control.enabled)} disabled={busy || !loaded || !data.ready} onChange={(_, enabled) => act('/settings', { enabled }, 'PUT')} />} />
                <Button variant="outlined" disabled={disabled} onClick={() => act('/snapshots')}>{t('backups.takeNow')}</Button>
            </Stack>
        </Paper>
        {progressJob && <Paper variant="outlined" sx={{ p: 2 }} role="status" aria-live="polite">
            <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('backups.progressTitle')} · {t(`backups.kind.${progressJob.kind}`, progressJob.kind)} · {t(`backups.state.${progressJob.status}`, progressJob.status)}</Typography>
            <BackupProgress job={progressJob} />
        </Paper>}
        {locked && <Alert severity="warning">{t('backups.locked')} ({data.control.lockJob})</Alert>}
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Typography variant="h6">{t('backups.restore')}</Typography>
                <TextField select fullWidth label={t('backups.snapshot')} value={snapshotId} onChange={e => setSnapshotId(e.target.value)} disabled={busy || !data.snapshots.length}>
                    {data.snapshots.map(s => <MenuItem key={s.snapshotId} value={s.snapshotId}>{jst(s.createdAt)} JST · {t(`backups.reason.${s.reason}`, s.reason)} · {s.documentCount !== undefined ? `${t('backups.documentCount', { count: Number(s.documentCount) })} · ` : ''}{Number(s.webEntryCount) > 0 ? `${t('backups.webEntryCount', { count: Number(s.webEntryCount) })} · ` : ''}{t('backups.storedObjectCount', { count: Number(s.storedObjectCount ?? s.fileCount) })}</MenuItem>)}
                </TextField>
                {!data.snapshots.length && <Typography color="text.secondary">{t('backups.empty')}</Typography>}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button color="warning" variant="contained" disabled={disabled || !snapshotId} onClick={() => setConfirm({ kind: 'full' })}>{t('backups.fullRestore')}</Button>
                    <Button color="error" variant="outlined" disabled={disabled || !selectedSnapshot} onClick={() => setConfirm({ kind: 'delete', snapshotId, createdAt: selectedSnapshot.createdAt })}>{t('backups.deleteSnapshot')}</Button>
                </Stack>
                <Typography variant="subtitle1">{t('backups.downloadFiles')}</Typography>
                <Stack component="form" onSubmit={searchFiles} direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField fullWidth type="search" label={t('backups.search')} value={query} onChange={e => {
                        setQuery(e.target.value);
                        setSearchRequest(null);
                        setSearchStatus('idle');
                        setFilePage({ files: [], total: 0 });
                        setOffset(0);
                    }} />
                    <Button type="submit" variant="outlined" disabled={!snapshotId || !query.trim() || searchStatus === 'loading'}>{t('backups.searchButton')}</Button>
                </Stack>
                <Typography variant="caption" color="text.secondary">{t('backups.downloadHint')}</Typography>
                {!hasSearch && <Typography variant="body2" color="text.secondary">{t('backups.searchHint')}</Typography>}
                {hasSearch && searchStatus === 'loading' && <LinearProgress aria-label={t('backups.loadingFiles')} />}
                {hasSearch && searchStatus === 'done' && filePage.total === 0 && <Typography variant="body2">{t('backups.noMatchingFiles')}</Typography>}
                {hasSearch && searchStatus === 'done' && filePage.total > 0 && <>
                    {['document'].map(category => {
                        const rows = fileRows.filter(file => (file.category || 'other') === category);
                        return rows.length > 0 && <Box key={category}>
                            <Typography variant="subtitle1">{t(`backups.resultGroups.${category}`)}</Typography>
                            <Box sx={{ overflowX: 'auto' }}><Table size="small">
                                <TableHead><TableRow><TableCell>{t('backups.documentName')}</TableCell><TableCell>{t('backups.filePath')}</TableCell><TableCell /></TableRow></TableHead>
                                <TableBody>{rows.map(file => <TableRow key={file.key}>
                                    <TableCell sx={{ overflowWrap: 'anywhere' }}>{file.documentName}
                                    </TableCell>
                                    <TableCell sx={{ wordBreak: 'break-all' }}>{file.key}</TableCell>
                                    <TableCell align="right"><Button disabled={busy} onClick={() => downloadFile(file.key)}>{t('backups.download')}</Button></TableCell>
                                </TableRow>)}</TableBody>
                            </Table></Box>
                        </Box>;
                    })}
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button disabled={!offset} onClick={() => setOffset(Math.max(0, offset - 50))}>{t('backups.previous')}</Button>
                        <Typography variant="body2">{offset + 1}–{Math.min(offset + 50, filePage.total)} / {filePage.total}</Typography>
                        <Button disabled={offset + 50 >= filePage.total} onClick={() => setOffset(offset + 50)}>{t('backups.next')}</Button>
                    </Stack>
                </>}
            </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, overflowX: 'auto' }}>
            <Typography variant="h6">{t('backups.jobs')}</Typography>
            <Table size="small"><TableHead><TableRow>{['started', 'operation', 'status', 'details'].map(key => <TableCell key={key}>{t(`backups.${key}`)}</TableCell>)}</TableRow></TableHead><TableBody>
                {data.jobs.map(job => <TableRow key={job.jobId}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{jst(job.createdAt)} JST</TableCell>
                    <TableCell>{t(`backups.kind.${job.kind}`, job.kind)}</TableCell>
                    <TableCell>{t(`backups.state.${job.status}`, job.status)}{['queued', 'running'].includes(job.status) && <BackupProgress job={job} compact />}</TableCell>
                    <TableCell><Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{job.error || job.jobId}</Typography>
                        {job.deletedObjectCount !== undefined && <Typography variant="caption" display="block">{t('backups.deletedObjects', { count: Number(job.deletedObjectCount) })}</Typography>}
                        {job.resultFileKey && <Typography variant="caption" display="block">{job.resultFileKey}</Typography>}
                        {job.safetySnapshotId && <Typography variant="caption">{t('backups.safety')}: {job.safetySnapshotId}</Typography>}
                        {(job.status === 'failed' || (job.status === 'running' && Number(job.heartbeatAt || 0) < Date.now() / 1000 - 180)) && <Button disabled={busy} onClick={() => act(`/jobs/${encodeURIComponent(job.jobId)}/retry`)}>{t('backups.retry')}</Button>}
                    </TableCell>
                </TableRow>)}
            </TableBody></Table>
        </Paper>
        <Dialog open={Boolean(confirm)} onClose={() => !busy && setConfirm(null)} fullWidth maxWidth="sm">
            <DialogTitle>{confirm?.kind === 'delete' ? t('backups.deleteSnapshot') : t('backups.fullRestore')}</DialogTitle>
            <DialogContent><Stack spacing={2}>
                <Typography>{clientId}</Typography>
                <Typography>{confirm?.kind === 'delete' ? `${jst(confirm.createdAt)} JST · ${confirm.snapshotId}` : selectedSnapshot ? `${jst(selectedSnapshot.createdAt)} JST` : ''}</Typography>
                <Alert severity="warning">{t(confirm?.kind === 'delete' ? 'backups.confirmDeleteSnapshot' : 'backups.confirmFull')}</Alert>
            </Stack></DialogContent>
            <DialogActions><Button disabled={busy} onClick={() => setConfirm(null)}>{t('backups.cancel')}</Button><Button variant="contained" color="warning" disabled={disabled} onClick={() => confirm.kind === 'delete' ? act(`/snapshots/${encodeURIComponent(confirm.snapshotId)}`, undefined, 'DELETE') : act('/restore', { snapshotId })}>{t(confirm?.kind === 'delete' ? 'backups.deleteSnapshot' : 'backups.confirm')}</Button></DialogActions>
        </Dialog>
    </Stack>;
}
